import { useEffect, useState } from "react";
import TabBar, { TabBarElement } from "../../common/TabBar";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import styles from "../css/LearnedTermsScreen.module.css";
import LearningRetry from "../model/LearningRetry";
import { useTranslation } from "react-i18next";
import TermListGrid from "../../term/custom_view/TermListGrid";
import Term, { getNextLevel } from "../../term/Term";
import { QuestionType } from "../../settings/model/QuestionType";
import { fetchLeanredTerms as fetchLeanredTermsFromAPI } from "../util/learningAPI";
import {
  updateTermLevel as updateTermLevelFromAPI,
  updateTermMark as updateTermMarkFromAPI,
  updateTerm as updateTermFromAPI,
  deleteTerm as deleteTermFromAPI,
  updateTermInformation,
} from "../../term/termAPI";
import {
  TermLevel,
  getCurrentLevelExp,
} from "../../term/custom_view/TermLevel";
import { useAppDispatch } from "../../../hook";
import { updateTerms } from "../../term/termSlice";
import {
  deleteAndReturnTerm,
  updateAndReturnTerm,
  updateAndReturnTermLevel,
  updateAndReturnTermMark,
  updateAndReturnTermMemo,
} from "../../term/terms";
import BasicMenu, { BasicMenuButton } from "../../common/BasicMenu";
import TermEditScreen from "../../term/view/TermEditScreen";
import { Status } from "../../common/Status";
import LoadingScreen from "../../common/LoadingScreen";
import OnevocaDialog from "../../common/OnevocaDialog";
import BigButton from "../../common/BigButton";
import LearningHistory from "../model/LearningHistory";
import { fetchSentence } from "../../sentence/service/sentenceAPI";
import { generateMoreSentence } from "../../sentence/service/moreSentence";
import SentenceHistory from "../../sentence/model/SentenceHistory";
import Language from "../../language/Language";
import { setUserCustomLanguage } from "../../sentence/manager/sentenceSettings";
import VoiceLanguageSelectScreen from "../../voice/view/VoiceLanguageSelectScreen";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import LearningReview from "./LearningReview";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import { useTerms } from "../../term/useTerms";
import { getLearningPronunciationPositionFromLocalStroage } from "../../settings/util/learningSettings";
import { getLearningRangeText } from "../util/learning";
import { TermSentenceWithStringValue } from "../../sentence/model/Sentence";

export interface LearnedTermsScreenProps {
  open: boolean;
  history: LearningHistory | null;
  onRetry: (item: LearningRetry) => void;
  onCancel: () => void;
}

export default function LearnedTermsScreen(props: LearnedTermsScreenProps) {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sentenceHistory] = useState(() => new SentenceHistory());

  const [terms] = useTerms();
  const termsDispatch = useAppDispatch();

  const [title, setTitle] = useState<string>("");
  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);
  const [
    isOpenSentenceLanguageSelectScreen,
    setIsOpenSentenceLanguageSelectScreen,
  ] = useState(false);

  const [type, setType] = useState<"_correct" | "_incorrect">("_correct");
  const [selectedTab, setSelectedTab] = useState<number>(2);
  const [tabBarItems, setTabBarItems] = useState<TabBarElement[]>([
    {
      id: 1,
      title: t("CorrectAnswer"),
      selected: false,
    },
    {
      id: 2,
      title: t("IncorrectAnswer"),
      selected: true,
    },
  ]);

  const [openLearningReview, setOpenLearningReview] = useState<boolean>(false);

  const [allTermsCount, setAllTermsCount] = useState<number>(0);
  const [correctTerms, setCorrectTerms] = useState<Term[]>([]);
  const [incorrectTerms, setIncorrectTerms] = useState<Term[]>([]);

  const [termEditStatus, setTermEditStatus] = useState(Status.Ready);
  const [termDeleteStatus, setTermDeleteStatus] = useState(Status.Ready);

  const [termEditSelectedItem, setTermEditSelectedItem] = useState<Term | null>(
    null
  );
  const [termDeleteSelectedItem, setTermDeleteSelectedItem] =
    useState<Term | null>(null);

  const [isOpenTermCellMoreMenu, setIsOpenTermCellMoreMenu] =
    useState<Term | null>(null);
  const [showingSentenceMoreMenu, setShowingSentenceMoreMenu] =
    useState<TermSentenceWithStringValue>();

  const getTermCellMoreButtons = (): BasicMenuButton[] => [
    {
      title: t("Edit"),
      handleOnClose: () => {
        const term = isOpenTermCellMoreMenu;
        if (term !== null) {
          setTermEditSelectedItem(term);
        }
        setIsOpenTermCellMoreMenu(null);
      },
    },
    {
      title: t("Delete"),
      handleOnClose: () => {
        const term = isOpenTermCellMoreMenu;
        if (term !== null) {
          setTermDeleteSelectedItem(term);
        }
        setIsOpenTermCellMoreMenu(null);
      },
    },
  ];

  const getTermCellSentenceMoreButtons = (): BasicMenuButton[] => [
    {
      title: t("ExampleSaveToTermMemo"),
      handleOnClose: () => {
        const item = showingSentenceMoreMenu;
        if (item !== undefined) {
          updateMemo(item);
        }
        setShowingSentenceMoreMenu(undefined);
      },
    },
  ];

  useEffect(() => {
    if (props.history?.range === undefined) return;
    setTitle(getLearningRangeText(props.history.range));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    setType(selectedTab === 1 ? "_correct" : "_incorrect");
    if (props.history !== null) {
      const newTabBarItems = [...tabBarItems];
      newTabBarItems[0].selected = selectedTab === 1;
      newTabBarItems[1].selected = selectedTab === 2;
      setTabBarItems(newTabBarItems);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    if (props.history !== null) {
      if (props.history.incorrectTermsCount !== 0) {
        setSelectedTab(2);
      } else {
        setSelectedTab(1);
      }
      const newTabBarItems = [...tabBarItems];
      newTabBarItems[0].selected = props.history.incorrectTermsCount === 0;
      newTabBarItems[1].selected = props.history.incorrectTermsCount !== 0;
      setTabBarItems(newTabBarItems);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.history]);

  useEffect(() => {
    if (props.history === null) {
      return;
    }
    setCorrectTerms([]);
    setIncorrectTerms([]);
    fetchLearnedTerms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, props.history]);

  useEffect(() => {
    switch (termEditStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        break;
    }
  }, [termEditStatus]);

  useEffect(() => {
    switch (termDeleteStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        break;
    }
  }, [termDeleteStatus]);

  useEffect(() => {
    setAllTermsCount(correctTerms.length + incorrectTerms.length);
  }, [correctTerms, incorrectTerms]);
  async function fetchLearnedTerms() {
    if (props.history === null) {
      return;
    }

    try {
      const result = await fetchLeanredTermsFromAPI(props.history.range);
      const newTerms = result.learnedTerms;
      setCorrectTerms(
        newTerms.filter((term) => {
          return term.isCorrectAnswer;
        })
      );
      setIncorrectTerms(
        newTerms.filter((term) => {
          return !term.isCorrectAnswer;
        })
      );
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function updateTermMarkToList(term: Term, isOn: boolean) {
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms[termIndex] = {
        ...newTerms[termIndex],
        isMarked: isOn,
      };
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }
  }

  function updateTermLevelToList(term: Term, toLevel: TermLevel) {
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms[termIndex] = {
        ...newTerms[termIndex],
        level: toLevel,
        exp: getCurrentLevelExp(toLevel),
      };
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }
  }

  function updateTermMarkToCurrentList(term: Term, isOn: boolean) {
    const newCorrectTerms = updateAndReturnTermMark(correctTerms, term, isOn);
    setCorrectTerms(newCorrectTerms);
  }

  function updateTermMarkToIncorrectList(term: Term, isOn: boolean) {
    const newIncorrectTerms = updateAndReturnTermMark(
      incorrectTerms,
      term,
      isOn
    );
    setIncorrectTerms(newIncorrectTerms);
  }

  function updateTermLevelToCurrentList(term: Term, toLevel: TermLevel) {
    const newCorrectTerms = updateAndReturnTermLevel(
      correctTerms,
      term,
      toLevel,
      getCurrentLevelExp(toLevel)
    );
    setCorrectTerms(newCorrectTerms);
  }

  function updateTermLevelToIncorrectList(term: Term, toLevel: TermLevel) {
    const newIncorrectTerms = updateAndReturnTermLevel(
      incorrectTerms,
      term,
      toLevel,
      getCurrentLevelExp(toLevel)
    );
    setIncorrectTerms(newIncorrectTerms);
  }

  function updateTermToList(term: Term) {
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms[termIndex] = term;
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }
  }

  function updateTermToCorrectList(term: Term) {
    const newCorrectTerms = updateAndReturnTerm(correctTerms, term);
    setCorrectTerms(newCorrectTerms);
  }

  function updateTermToIncorrectList(term: Term) {
    const newIncorrectTerms = updateAndReturnTerm(incorrectTerms, term);
    setIncorrectTerms(newIncorrectTerms);
  }

  async function updateTerm(term: Term) {
    setTermEditStatus(Status.Loading);
    try {
      await updateTermFromAPI(term);
      updateTermToList(term);
      updateTermToCorrectList(term);
      updateTermToIncorrectList(term);
      setTermEditStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermEditStatus(Status.Ready);
    }
  }

  async function updateMemo(item: TermSentenceWithStringValue) {
    setTermEditStatus(Status.Loading);
    try {
      await updateTermInformation(
        item.term.id,
        undefined,
        undefined,
        item.stringValue,
        undefined
      );
      updateTermToList({ ...item.term, memo: item.stringValue });
      setCorrectTerms((prev) =>
        updateAndReturnTermMemo(prev, item.term.id, item.stringValue)
      );
      setIncorrectTerms((prev) =>
        updateAndReturnTermMemo(prev, item.term.id, item.stringValue)
      );
      setTermEditStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermEditStatus(Status.Ready);
    }
  }

  async function deleteTerm(term: Term) {
    setTermDeleteStatus(Status.Loading);
    try {
      await deleteTermFromAPI(term);
      deleteTermToList(term);
      deleteTermToCorrectList(term);
      deleteTermToIncorrectList(term);
      setTermDeleteStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setTermDeleteStatus(Status.Ready);
    }
  }

  function deleteTermToList(term: Term) {
    const termIndex = terms.findIndex((t) => t.id === term.id);
    if (termIndex !== -1) {
      const newTerms = [...terms];
      newTerms.splice(termIndex, 1);
      const serializedTerms = newTerms.map((term) => Term.toJson(term));
      termsDispatch(updateTerms(serializedTerms));
    }
  }

  function deleteTermToCorrectList(term: Term) {
    const newCorrectTerms = deleteAndReturnTerm(correctTerms, term);
    setCorrectTerms(newCorrectTerms);
  }

  function deleteTermToIncorrectList(term: Term) {
    const newIncorrectTerms = deleteAndReturnTerm(incorrectTerms, term);
    setIncorrectTerms(newIncorrectTerms);
  }

  function handleChangeTabBarSelected(id: number) {
    setSelectedTab(id);
  }

  function handleOnClickTermMark(term: Term) {
    try {
      const newMark = !term.isMarked;
      updateTermMarkFromAPI(term, newMark);
      updateTermMarkToList(term, newMark);
      updateTermMarkToCurrentList(term, newMark);
      updateTermMarkToIncorrectList(term, newMark);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function handleOnClickTermLevel(term: Term) {
    try {
      const nextLevel = getNextLevel(term.level);
      updateTermLevelFromAPI(term, nextLevel);
      updateTermLevelToList(term, nextLevel);
      updateTermLevelToCurrentList(term, nextLevel);
      updateTermLevelToIncorrectList(term, nextLevel);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  async function generateSentence(term: Term) {
    switch (type) {
      case "_correct":
        setCorrectTerms((prevTerms) => {
          const index = prevTerms.findIndex((t) => t.id === term.id);
          if (index === -1) return prevTerms;

          const updated = [...prevTerms];
          updated[index] = {
            ...updated[index],
            isGeneratingSentence: true,
          };
          return updated;
        });
        break;
      case "_incorrect":
        setIncorrectTerms((prevTerms) => {
          const index = prevTerms.findIndex((t) => t.id === term.id);
          if (index === -1) return prevTerms;

          const updated = [...prevTerms];
          updated[index] = {
            ...updated[index],
            isGeneratingSentence: true,
          };
          return updated;
        });
        break;
    }

    try {
      const languageCode =
        term.group.length !== 0 ? await fetchGroupLanguageCode(term.group) : "";
      const sentence = await fetchSentence(
        term.term,
        languageCode,
        sentenceHistory
      );

      switch (type) {
        case "_correct":
          setCorrectTerms((prevTerms) => {
            const index = prevTerms.findIndex((t) => t.id === term.id);
            if (index === -1) return prevTerms;

            const updated = [...prevTerms];
            updated[index] = {
              ...updated[index],
              sentence,
              isGeneratingSentence: false,
            };
            return updated;
          });
          break;
        case "_incorrect":
          setIncorrectTerms((prevTerms) => {
            const index = prevTerms.findIndex((t) => t.id === term.id);
            if (index === -1) return prevTerms;

            const updated = [...prevTerms];
            updated[index] = {
              ...updated[index],
              sentence,
              isGeneratingSentence: false,
            };
            return updated;
          });
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function deleteSentence(term: Term) {
    const termIndex =
      type === "_correct"
        ? correctTerms.findIndex((t) => t.id === term.id)
        : incorrectTerms.findIndex((t) => t.id === term.id);

    switch (type) {
      case "_correct":
        if (termIndex !== -1) {
          const newTerms = [...correctTerms];
          const { sentence, ...rest } = newTerms[termIndex];
          const newTerm = { ...rest };

          newTerms[termIndex] = newTerm;

          setCorrectTerms(newTerms);
        }
        break;
      case "_incorrect":
        if (termIndex !== -1) {
          const newTerms = [...incorrectTerms];
          const { sentence, ...rest } = newTerms[termIndex];
          const newTerm = { ...rest };

          newTerms[termIndex] = newTerm;

          setIncorrectTerms(newTerms);
        }
        break;
    }
  }

  function close() {
    setSelectedTab(1);
    props.onCancel();
  }

  function handleOnClickTermCellMoreButton(
    event: React.MouseEvent<HTMLDivElement>,
    term: Term
  ) {
    setAnchorEl(event.currentTarget);
    setIsOpenTermCellMoreMenu(term);
  }

  function handleOnClickSentenceMoreButton(
    event: React.MouseEvent<HTMLDivElement>,
    item: TermSentenceWithStringValue
  ) {
    setAnchorEl(event.currentTarget);
    setShowingSentenceMoreMenu(item);
  }

  function handleOnClickGenerateSentenceButton(term: Term) {
    if (term.sentence === undefined) {
      generateSentence(term);
    } else {
      deleteSentence(term);
    }
  }

  function handleOnClickSentenceLanguageButton(term: Term) {
    setSelectedTerm(term);
    setIsOpenSentenceLanguageSelectScreen(true);
  }

  function handleOnClickMoreSentenceButton(term: Term) {
    generateMoreSentence(
      term,
      type === "_correct" ? setCorrectTerms : setIncorrectTerms,
      sentenceHistory
    );
  }

  function handleOnClickReviewButton() {
    setOpenLearningReview(true);
  }

  function updateSentenceLanguage(language: Language) {
    setUserCustomLanguage(language.code);

    if (selectedTerm != null) {
      generateSentence(selectedTerm);
    }
  }

  if (props.open === false) {
    return null;
  }

  return (
    <>
      <div className={styles.stack}>
        <div
          className={styles.screen}
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
          }}
        >
          <div className={styles.container}>
            <div className={styles.contentsContainer}>
              <ContainerNavigationView
                open={true}
                title={title}
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={() => {
                  close();
                }}
              />
              <TabBar
                items={tabBarItems}
                onChangeSelectedItem={handleChangeTabBarSelected}
              />
              <TermListGrid
                terms={type === "_correct" ? correctTerms : incorrectTerms}
                handleOnClick={() => {}}
                handleOnClickMarkButton={handleOnClickTermMark}
                handleOnClickLevelButton={handleOnClickTermLevel}
                handleOnClickMoreButton={(event, term) => {
                  handleOnClickTermCellMoreButton(event, term);
                }}
                handleOnClickGenerateSentenceButton={
                  handleOnClickGenerateSentenceButton
                }
                handleOnClickSentenceLanguageButton={
                  handleOnClickSentenceLanguageButton
                }
                handleOnClickMoreSentenceButton={
                  handleOnClickMoreSentenceButton
                }
                handleOnClickSentenceMoreButton={
                  handleOnClickSentenceMoreButton
                }
                config={{
                  style: "learnedTerms",
                  questionType: QuestionType.termFirst,
                  pronunciationPosition:
                    getLearningPronunciationPositionFromLocalStroage(),
                  isSelectionMode: false,
                  isShowDetailMode: true,
                  hideMoreButton: true,
                }}
              />
            </div>
            <div className={styles.bottomButtonContainer}>
              <BigButton
                title={t("LearningReviewButton")}
                variant={allTermsCount === 0 ? "disable" : "normal"}
                handleClick={() => {
                  if (allTermsCount === 0) return;
                  handleOnClickReviewButton();
                }}
              />
            </div>
          </div>
          <BasicMenu
            isOpen={isOpenTermCellMoreMenu !== null}
            anchorEl={anchorEl}
            buttons={getTermCellMoreButtons()}
            handleOnClose={() => {
              setIsOpenTermCellMoreMenu(null);
            }}
          />
          <BasicMenu
            isOpen={showingSentenceMoreMenu !== undefined}
            anchorEl={anchorEl}
            buttons={getTermCellSentenceMoreButtons()}
            handleOnClose={() => setShowingSentenceMoreMenu(undefined)}
          />
          <TermEditScreen
            open={termEditSelectedItem !== null}
            term={termEditSelectedItem!}
            handleOnSave={(term: Term) => updateTerm(term)}
            handleOnClose={() => {
              setTermEditSelectedItem(null);
            }}
          />
          <OnevocaDialog
            open={termDeleteSelectedItem !== null}
            title={null}
            message={t("areyousuremesssage")}
            buttons={[
              {
                title: t("Delete"),
                style: "solid",
                onClick: () => {
                  const term = termDeleteSelectedItem;
                  if (term !== null) {
                    setTermDeleteSelectedItem(null);
                    deleteTerm(term);
                  }
                },
              },
              {
                title: t("Cancel"),
                style: "normal",
                onClick: () => {
                  setTermDeleteSelectedItem(null);
                },
              },
            ]}
          />
          {isLoading === true && <LoadingScreen />}
        </div>
        <LearningReview
          open={openLearningReview}
          range={props.history?.range}
          onClickStartButton={(learningRetry: LearningRetry) => {
            props.onRetry(learningRetry);
          }}
          onClose={() => {
            setOpenLearningReview(false);
          }}
        />
        <VoiceLanguageSelectScreen
          open={isOpenSentenceLanguageSelectScreen === true}
          handleOnClickLanguage={updateSentenceLanguage}
          handleOnClose={() => {
            setIsOpenSentenceLanguageSelectScreen(false);
          }}
        />
      </div>
    </>
  );
}
